/**
 * SFU-1 (L3 safety net): recover from stale-chunk errors after a deploy.
 *
 * When a new build is deployed, an already-open tab may try to lazy-load a
 * chunk whose content-hashed filename no longer exists on the server, which
 * Vite surfaces as a `vite:preloadError` event. Instead of letting that bubble
 * into a white screen, we reload the page once to pick up the fresh
 * `index.html` (and its new chunk hashes).
 *
 * The reload is guarded by a `sessionStorage` flag so a genuinely-missing chunk
 * (offline, or a new build that is itself broken) cannot cause an infinite
 * reload loop: at most one automatic reload per episode. The flag is cleared a
 * few seconds after a healthy load, so a later unrelated deploy can recover
 * again.
 */
const RELOAD_GUARD_KEY = 'tt:chunk-reload';
const CLEAR_DELAY_MS = 5000;

/**
 * Registers the chunk-load recovery listeners on `window`.
 * @param reload - reload strategy (injectable for tests); defaults to a full page reload.
 * @returns a disposer that removes the listeners (used by tests; the app ignores it).
 */
export function installChunkReloadGuard(
  reload: () => void = () => window.location.reload(),
): () => void {
  const onPreloadError = (event: Event) => {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY)) return; // already reloaded once — don't loop
    sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
    event.preventDefault(); // suppress Vite's default throw so we control recovery
    reload();
  };

  const onLoad = () => {
    window.setTimeout(() => sessionStorage.removeItem(RELOAD_GUARD_KEY), CLEAR_DELAY_MS);
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  window.addEventListener('load', onLoad);

  return () => {
    window.removeEventListener('vite:preloadError', onPreloadError);
    window.removeEventListener('load', onLoad);
  };
}
